/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import StyledProductCard from './ProductCard.styled';
import ColorSelector from './ColorSelector';
import WeightSelector from './WeightSelector';
import { Link } from 'react-router-dom';
import CardButton from './CardButton';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { isInteger } from 'lodash';
import { ThemePramsContext } from '../../App';
import { pushToDataLayer } from '../../helpers/analytics';

const ProductCard = React.memo((props) => {
  const {
    className,
    lineage,
    productName,
    imgSrc,
    price,
    salesPrice,
    productId,
    addCart,
    variationType,
    children,
    onSelectProp,
    childId,
    loading,
    brand,
    netWeight,
    netWeight_g,
    placeHolderHeight,
    abbreviated,
    onClick,
    THC,
    CBD,
    productUrl,
    quantity,
    showLowQuantity,
    active,
    category,
  } = props;
  const [cartSuccess, setCartSuccess] = useState(false);
  const [addProductId, setAddProductId] = useState('');
  const [loadImage, setLoadImage] = useState(false);

  const isSativa = ['Sativa', 'Sativa Hybrid', 'Sativa Dominate'];
  const isIndica = ['Indica', 'Indica Hybrid', 'Indica Dominate'];
  const addCartFunc = (id, count) => {
    setAddProductId(id);
    addCart(id, count, {
      className,
      name: productName,
      imgSrc,
      price,
      salesPrice,
      productId,
      variationType,
      brand,
      netWeight,
      abbreviated,
      quantity: 1,
    });

    // GA/GTM integration
    pushToDataLayer({ ecommerce: null });
    const payload = {
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: parseFloat(salesPrice || price),
        items: [
          {
            item_id: String(id),
            item_name: productName,
            item_category: category || '',
            price: parseFloat(salesPrice || price),
            quantity: 1,
          },
        ],
      },
    };
    pushToDataLayer(payload);
  };
  useEffect(() => {
    if ((addProductId === productId || addProductId === childId) && loading) {
      const timer = setTimeout(() => {
        setCartSuccess(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [addProductId]);

  useEffect(() => {
    if (cartSuccess) {
      const timer = setTimeout(() => {
        setCartSuccess(false);
        setAddProductId('');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cartSuccess]);

  const onError = () => {
    setLoadImage(false);
  };

  const onLoad = () => {
    setLoadImage(true);
  };

  const calculatePercentage = (price, salesPrice) => {
    return (((price - salesPrice) / price) * 100).toFixed();
  };

  const countDecimals = (value) => {
    if (value === null) return '';
    let price = Number(value);
    return isInteger(price) ? price : price.toFixed(2);
  };

  const weight_With_WeightG = `${netWeight} ${netWeight_g}`;

  const {
    productDetailsConfig: { productCardConfig },
  } = useContext(ThemePramsContext);

  return (
    <StyledProductCard
      className={className}
      placeHolderHeight={placeHolderHeight}
      themeProps={productCardConfig?.productCardConfigStyled}
    >
      <Link
        to={`/products/${childId ? `${productUrl}?child=${childId}` : productUrl}`}
        className={`product-card__link-to ${active ? '' : 'disabled-link'}`}
        onClick={onClick && onClick}
      >
        <div className={`product-card__media ${loadImage ? 'loaded-image' : ''}`}>
          <LazyLoadImage
            src={imgSrc}
            alt="product"
            onError={() => onError()}
            afterLoad={() => onLoad()}
            threshold={500}
          />
          {!loadImage && <div className="no-image-placeholder" />}
          <p className="t-hidden">Product image</p>
        </div>
        <div className="product-card__details">
          <p className={`product-card__eyebrow ${productCardConfig.className1}`}>{brand}</p>
          <div className={`product-card__price ${Number(quantity) === 0 && 'is-low-quantity'}`}>
            <div className="product-card__sales">
              {!!salesPrice && (
                <p className={`product-card__sale__prev ${productCardConfig.className2}`}>
                  <del>${countDecimals(price)}</del>
                </p>
              )}
              {!!salesPrice && (
                <p className={`product-card__sale__new ${productCardConfig.className3}`}>
                  ${countDecimals(salesPrice)}
                </p>
              )}
              {!salesPrice && <p className={productCardConfig.className2}>${countDecimals(price)}</p>}
            </div>
            {!!salesPrice && (
              <div className={`product-price-with-percentage ${productCardConfig.className4}`}>
                Save {calculatePercentage(price, salesPrice)}%
              </div>
            )}
          </div>
          {lineage && (
            <p
              className={`product-card__badge 
                ${productCardConfig.className5}
                ${isSativa.includes(lineage) && 'is-sativa'} 
                ${isIndica.includes(lineage) && 'is-indica'}
                ${lineage === 'Hybrid' && 'is-hybrid'} 
                ${lineage === 'High CBD' && 'is-cbd'}
               `}
            >
              {lineage}
            </p>
          )}
          <p className={`product-card__title  ${productCardConfig.className6}`}>{productName}</p>
          {THC || CBD ? (
            <p className={`product-card__percent ${productCardConfig.className7}`}>
              {THC ? (
                <span
                  data-testid="THC-content"
                  className={`product-card__percent__node ${productCardConfig.className8}`}
                >
                  <span className={`t-bold ${productCardConfig.className9}`}>THC&nbsp;&nbsp;</span>
                  {THC}%
                </span>
              ) : (
                ''
              )}
              {CBD ? (
                <span
                  data-testid="CBD-content"
                  className={`product-card__percent__node ${productCardConfig.className8}`}
                >
                  <span className={`t-bold ${productCardConfig.className9}`}>CBD&nbsp;&nbsp;</span>
                  {CBD}%
                </span>
              ) : (
                ''
              )}
            </p>
          ) : (
            ''
          )}
        </div>
      </Link>

      {abbreviated ? (
        ''
      ) : (
        <>
          <div className="product-card__options">
            {variationType && (variationType === 'color' || variationType === 'weight') && children ? (
              <>
                {variationType && variationType === 'color' && (
                  <>
                    <h3 className="t-hidden">Type:</h3>
                    <ColorSelector
                      children={children}
                      onSelectProp={onSelectProp}
                      productId={productId}
                      childId={childId}
                      opacity={quantity ? '1' : '0.3'}
                    />
                  </>
                )}

                {variationType && variationType === 'weight' && (
                  <>
                    <h3 className="t-hidden">Weight:</h3>
                    <WeightSelector
                      children={children}
                      onSelectProp={onSelectProp}
                      productId={productId}
                      childId={childId}
                    />
                  </>
                )}
              </>
            ) : (
              ''
            )}

            {!variationType &&
            !(variationType === 'color' || variationType === 'weight') &&
            netWeight &&
            netWeight_g ? (
              <>
                <h3 className="t-hidden">Weight:</h3>
                <WeightSelector
                  children={[{ variation: weight_With_WeightG, id: 1 }]}
                  onSelectProp={() => {}}
                  childId={1}
                />
              </>
            ) : (
              ''
            )}
          </div>

          {showLowQuantity && (
            <div className="t-body-small only-left">
              {quantity != 0 ? `Only ${quantity} left in stock!` : 'Out Of Stock'}
            </div>
          )}

          <div className="product-card__shop">
            <CardButton
              addProductId={addProductId}
              productId={productId}
              productPrice={price}
              childId={childId}
              loading={addProductId === productId || addProductId === childId}
              addCartFunc={addCartFunc}
              cartSuccess={cartSuccess}
              variationType={variationType}
              quantity={quantity}
            />
          </div>
        </>
      )}
    </StyledProductCard>
  );
});

ProductCard.propTypes = {
  className: PropTypes.string,
  productName: PropTypes.string,
  price: PropTypes.number.isRequired,
  salesPrice: PropTypes.number,
  imgSrc: PropTypes.string,
  productId: PropTypes.string,
  addCart: PropTypes.func,
  abbreviated: PropTypes.bool,
  THC: PropTypes.number,
  CBD: PropTypes.number,
  netWeight: PropTypes.number,
  netWeight_g: PropTypes.string,
  lineage: PropTypes.string,
  variationType: PropTypes.string,
  children: PropTypes.array,
  onSelectProp: PropTypes.func,
  childId: PropTypes.string,
  loading: PropTypes.bool,
  brand: PropTypes.string,
  placeHolderHeight: PropTypes.number,
  onClick: PropTypes.func,
  productUrl: PropTypes.string,
  quantity: PropTypes.string,
  showLowQuantity: PropTypes.bool,
  active: PropTypes.bool,
};

ProductCard.defaultProps = {
  active: true,
};

export default ProductCard;
